import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import { DualListItem } from "components/DualListSelector";
import DualListSelectorSearchableForm from "components/DualListSelector/DualListSelectorSearchableForm";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  BaseFilter,
  ListRequestOfBaseFilter,
  Profession,
  SkuContentRule,
  SkuContentRuleProfession,
  Skus,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";

type SkuRuleDetailProfessionFormProps = {
  sku: Skus;
  skuContentRule: SkuContentRule;
  readonly?: boolean;
  index: number;
} & SkuFormProps;

const SkuRuleDetailProfessionForm = ({
  sku,
  skuContentRule,
  readonly = false,
  index,
  onDirty,
  onClean,
  onSkuUpdated,
}: SkuRuleDetailProfessionFormProps) => {
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [options, setOptions] = useState<DualListItem[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<DualListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const skuContentRuleProfessionsResult = useQuery(
    ["skuRuleDetails", sku.id, skuContentRule.id],
    () =>
      skuService.getSkuContentRuleProfessions(
        sku.id!,
        skuContentRule.id,
        AdminListRequestOfBaseFilter.fromJS({ sortField: "profession.name" })
      )
  );

  const fetchedProfessions = useQuery(["professions", searchTerm], () =>
    skuService.getProfessions(
      ListRequestOfBaseFilter.fromJS({
        sortField: "name",
        parameters: BaseFilter.fromJS({
          searchText: searchTerm === "" ? undefined : searchTerm,
        }),
      })
    )
  );

  const mapProfession = (skus: Profession[]) => {
    return skus.map((s) => new DualListItem(s.id, s.name));
  };

  const mapSkuContentRuleProfession = (skus: SkuContentRuleProfession[]) => {
    return skus.map((s) => new DualListItem(s.professionId, s.name));
  };

  useEffect(() => {
    setOptions(
      fetchedProfessions.data
        ? mapProfession(fetchedProfessions.data.data!)
        : []
    );
  }, [fetchedProfessions.data, fetchedProfessions.data?.data]);

  useEffect(() => {
    setSelectedOptions(
      skuContentRuleProfessionsResult.data
        ? mapSkuContentRuleProfession(
            skuContentRuleProfessionsResult.data.data!
          )
        : []
    );
  }, [
    skuContentRuleProfessionsResult.data,
    skuContentRuleProfessionsResult.data?.data,
  ]);

  const onCleanForm = useCallback(() => {
    setSelectedOptions(
      skuContentRuleProfessionsResult.data
        ? mapSkuContentRuleProfession(
            skuContentRuleProfessionsResult.data.data!
          )
        : []
    );

    onClean && onClean();
  }, [skuContentRuleProfessionsResult.data, onClean]);

  if (skuContentRuleProfessionsResult.isLoading) return <Loader />;
  if (skuContentRuleProfessionsResult.isError) {
    console.log(skuContentRuleProfessionsResult.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRuleDetailsError" />
        </Alert>
      </Box>
    );
  }

  const onProfessionSelectionChange = (selected: DualListItem[]) => {
    selected.sort((a, b) => a.text.localeCompare(b.text));
    setSelectedOptions(selected);
  };

  const onSubmit = async () => {
    try {
      await registerPromise(
        skuService.saveSkuContentRuleProfessions(
          sku.id!,
          skuContentRule.id,
          selectedOptions.map((s) => s.id)
        )
      );
      queryClient.invalidateQueries([
        "skuRuleDetails",
        sku.id,
        skuContentRule.id,
      ]);

      onSkuUpdated && onSkuUpdated();
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "ruleUpdatedMessage" }))
      );
    } catch (err) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <DualListSelectorSearchableForm
        setSearchTerm={setSearchTerm}
        options={options}
        selectedOptions={selectedOptions}
        isFetching={fetchedProfessions.isFetching}
        readonly={readonly}
        index={index}
        onSubmit={onSubmit}
        onChange={onProfessionSelectionChange}
        onClean={onCleanForm}
        onDirty={onDirty}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default SkuRuleDetailProfessionForm;
